/**
 * Счётчики шапки: опыт, билеты и серия.
 *
 * Три коротких числа, за которыми возвращаются завтра. Иконка — для
 * быстрого узнавания, число — для точности, а осмысленная подпись уходит
 * в `sr-only`: скринридер прочитает «серия 4 дня», а не «огонёк четыре».
 *
 * На самом узком экране прячется только билеты: опыт и серия — ядро
 * мотивации, они остаются всегда.
 */

import { useProfile } from '../../hooks/useProfile';
import { IconBolt, IconFlame, type AppIcon } from '../icons';
import { useTranslation } from 'react-i18next';

interface ChipProps {
  readonly icon: AppIcon;
  readonly tone: string;
  readonly value: number;
  /** Подпись для скринридера; визуально скрыта. */
  readonly label: string;
  readonly className?: string;
}

function Chip({ icon: ChipIcon, tone, value, label, className }: ChipProps) {
  return (
    <span
      className={[
        // Высота как у круглых кнопок шапки (44px): ряд справа ровный.
        'inline-flex h-11 items-center gap-1.5 rounded-full border border-line bg-surface/95 px-3.5 shadow-soft',
        'font-heading text-sm font-bold text-heading',
        className ?? '',
      ]
        .filter((part) => part.length > 0)
        .join(' ')}
    >
      <ChipIcon size={18} stroke={2} className={tone} aria-hidden="true" />
      <span className="tabular-nums">{value}</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function StatusChips() {
  const profile = useProfile();
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2">
      <Chip icon={IconBolt} tone="text-accent" value={profile.xp} label={t('status.xp', { count: profile.xp })} />
      <Chip
        icon={IconFlame}
        tone="text-accent-dark"
        value={profile.streak}
        label={t('status.streak', { count: profile.streak })}
      />
    </div>
  );
}
